/* eslint-disable react-native/no-inline-styles */
import React, {FC, useCallback, useEffect, useMemo, useState} from 'react';
import VideoPlayer, {
  PlayerTypeEnum,
} from 'design-system/src/Widget/Player/VideoPlayer/VideoPlayer.web';
import useVideoPlayerLogic, {VideoPlayerParams} from './useVideoPlayerLogic';
import {CustomActivityIndicator} from 'design-system/src/Components/CustomActivityIndicator';
import useParams from '@src/Hooks/useParams';
import PaywallChecker from '@src/Umbraco/Components/PaywallChecker/PaywallChecker';
import useViewPort from 'design-system/src/Hooks/useViewPort';
import useMember from '@src/Store/selectors/useMember';
import {EQuality} from '@src/Navigation/Profile/ProfileSetting/ProfileSetting';
import usePaywall from '@src/Hooks/usePaywall';
import useAppLocation from '@src/App/Route/AppRouterContext/useAppLocation';
import {
  IGetMainContentListByIdQuery,
  IPaywallFragmentFragment,
  useGetMainContentListByIdLazyQuery,
} from '@src/Api/Graphql/gql/types.generated';
import PlayerPaywall from '@src/Umbraco/Components/PlayerPaywall/PlayerPaywall';
import {useTranslation} from 'gatsby-plugin-react-i18next';
import useAppVideoPlayerStorage from './useAppVideoPlayerStorage';
import {RouteTypename} from '@src/Navigation/type';
import VideoErrorDisplay from './components/VideoErrorDisplay/VideoErrorDisplay';
import {LiveEventInfos} from './interfaces/liveEventInfos.interface';
import {PlayerPaywallTrackingProps} from '@src/Services/Amplitude/hooks/tracker';

type ContentItem = NonNullable<
  IGetMainContentListByIdQuery['allContent']
>['items'][number];

const getCoverUrlFromItem = (item: ContentItem) => {
  const covers: Array<{
    __typename: string;
    content?: {desktopImage?: {url?: string}};
  }> = (item && 'cover' in item && (item as any).cover) || [];
  const coverObj = covers.find(cover => cover.__typename === 'BlockListItem');

  const coverUrl = coverObj?.content?.desktopImage?.url ?? '';
  return coverUrl;
};

const getEventInfos = (item?: ContentItem): LiveEventInfos => {
  if (!item) {
    return {description: '', title: '', endDate: '', startDate: ''};
  }

  const {
    description = '',
    title = '',
    endDate = '',
    startDate = '',
  } = item as Partial<Record<keyof LiveEventInfos, string>>;

  return {
    description,
    title,
    endDate,
    startDate,
  };
};

const AppVideoPlayer: FC = () => {
  const location = useAppLocation();
  const [videoPlayerArgs, setVideoPlayerArgs] = useAppVideoPlayerStorage();
  const locationParams: any = useParams();
  const [fetchContent, {loading: getContentLoading}] =
    useGetMainContentListByIdLazyQuery();
  const member = useMember();
  const {t} = useTranslation('translation');
  const [umbData, setUmbData] = useState<IGetMainContentListByIdQuery>();
  const [coverUrl, setCoverUrl] = useState('');
  const [eventInfos, setEventInfos] = useState<LiveEventInfos>({
    description: '',
    title: '',
  });
  const [cooldown, setCooldown] = useState(false);
  const [cooldownLeft, setCooldownLeft] = useState(0);
  const [watchTime, setWatchTime] = useState(0);

  const params = {...videoPlayerArgs, ...locationParams};
  const contentType: RouteTypename = params.__typename;
  const paramsSource =
    contentType === 'LiveEvent' || contentType === 'Channel'
      ? 'livestream'
      : params?.source || 'vimeo';
  const {canSeeContent, getPremiumPreviewSettings} = usePaywall();
  const videoPlayerInfos = useVideoPlayerLogic({
    ...(params || {}),
    paywallData: params?.paywallData || {
      accessType: params?.accessType,
      paywall: params?.paywall,
    },
    source: paramsSource,
  } as VideoPlayerParams);
  const {
    liveEvent,
    isLoading,
    handleOnVideoPlay,
    refCurrentChannel,
    handleOnVideoLoad,
    handleOnGetDuration,
    handleOnBack,
    handleOnPlayEnd,
    handleOnTimeProgress,
    handleOnLanguageChange,
    handleOnSpeedChange,
    handleOnSkipChange,
    onNextEpisode,
    handleOnResolutionChange,
    currentTime,
    displayPaywall,
    paywallSetting,
    content,
    isLive,
    source,
    previewLoading,
  } = videoPlayerInfos;
  const paywallData = params?.paywallData;

  const currentIndex = refCurrentChannel.current || 0;
  const currentSource = liveEvent?.sourceList?.[currentIndex];
  const {viewportHeight} = useViewPort();
  const playerType: PlayerTypeEnum =
    paramsSource === 'livestream' ? 'live' : 'standard';
  const url = (currentSource as any)?.uri || '';
  const poster = (currentSource as any)?.poster || '';
  const accessType =
    (currentSource && currentSource.accessData?.accessType) ||
    paywallData?.accessType;
  const hasAccess: boolean = accessType ? canSeeContent(accessType) : true;
  const canPreview = getPremiumPreviewSettings(
    paywallData?.paywall as IPaywallFragmentFragment,
  )?.canPreview;

  const fetch = useCallback(async () => {
    if (!locationParams?.id) return;
    const {data: contentData} = await fetchContent({
      variables: {id: [locationParams.id]},
    });
    if (contentData) setUmbData(contentData);
  }, [fetchContent, locationParams?.id]);

  const refreshAll = useCallback(() => {
    fetch();

    setUmbData(undefined);
    setCoverUrl('');
    setEventInfos({description: '', title: '', endDate: '', startDate: ''});
    setCooldown(true);
    setCooldownLeft(60);
  }, [fetch]);

  useEffect(() => {
    fetch();
  }, [locationParams?.id]);

  useEffect(() => {
    if (!cooldown) return;
    if (cooldownLeft <= 0) {
      setCooldown(false);
      return;
    }
    const interval = setInterval(() => {
      setCooldownLeft(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          setCooldown(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [cooldown, cooldownLeft]);

  useEffect(() => {
    if (!umbData) return;

    const {allContent} = umbData;
    const items = allContent?.items || [];

    const currentItem: ContentItem | undefined = items.find(
      (item): item is ContentItem => item?.id === locationParams?.id,
    );

    setCoverUrl(getCoverUrlFromItem(currentItem));
    setEventInfos({
      ...getEventInfos(currentItem),
      onRefresh: refreshAll,
    });
  }, [umbData]);

  useEffect(() => {
    if (
      locationParams &&
      ((!!locationParams.liveEventId &&
        locationParams.liveEventId !== videoPlayerArgs.liveEventId) ||
        (!!locationParams.videoId &&
          locationParams.videoId !== videoPlayerArgs.videoId) ||
        (!!locationParams.videoUrl &&
          locationParams.videoUrl !== videoPlayerArgs.videoUrl) ||
        (!!locationParams.sourceUrl &&
          locationParams.sourceUrl !== videoPlayerArgs.sourceUrl))
    ) {
      setVideoPlayerArgs(locationParams);
    }
  }, [locationParams, videoPlayerArgs]);

  useEffect(() => {
    // update submenu left title
    if (currentSource && location) {
      const title = currentSource?.description;
      location.state = {
        ...(location.state || {}),
        title: title,
        params: {
          ...((location.state as any)?.params || {}),
          title: title,
        },
      };
    }
  }, [currentSource, location]);

  const maxPreview = isLive
    ? paywallSetting.previewDurationTv
    : paywallSetting.previewDurationVod;
  const paywallCheckerTrackingData: PlayerPaywallTrackingProps = useMemo(() => {
    if (!content) {
      return {
        maxPreviewDurationMinutes: 0,
        watchedTimeSeconds3Decimals: watchTime,
        id: 'VideoPlayerDefaultContentTracking',
        title: 'VideoPlayerDefaultContentTracking',
        __typename: 'VODItem',
        isTrackingReady: false,
        source_url: window.location.href,
      };
    }

    const {id, title, __typename} = content;

    return {
      maxPreviewDurationMinutes: maxPreview,
      watchedTimeSeconds3Decimals: watchTime,
      id,
      title,
      __typename,
      source_url: window.location.href,
    };
  }, [maxPreview, watchTime, content]);

  const onTimeProgress = (seconds: number) => {
    setWatchTime(prev => prev + seconds);
    handleOnTimeProgress && handleOnTimeProgress(seconds);
  };

  if (!url && !isLoading) {
    return (
      <VideoErrorDisplay
        message={t('No video found')}
        viewportHeight={viewportHeight}
        isLiveStream={source === 'livestream'}
        posterUrl={coverUrl || poster || ''}
        eventInfos={eventInfos}
        cooldown={cooldown}
        cooldownLeft={cooldownLeft}
      />
    );
  }

  if (isLoading || previewLoading) {
    return (
      <div
        style={{
          width: '100%',
          height: viewportHeight,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}>
        <CustomActivityIndicator />
      </div>
    );
  }

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        position: 'fixed',
        zIndex: 49,
      }}>
      <PaywallChecker
        accessType={accessType}
        trackingData={paywallCheckerTrackingData}>
        <VideoPlayer
          disableCast={!hasAccess}
          paywallContent={
            <PlayerPaywall
              isLive={isLive || source === 'livestream'}
              setting={paywallSetting}
              content={content}
              secondsWatched={watchTime}
              displayPaywall={Boolean(displayPaywall)}
            />
          }
          displayPaywall={displayPaywall}
          sourceList={liveEvent?.sourceList || []}
          title={liveEvent?.title || ''}
          onNext={onNextEpisode}
          onGoBack={handleOnBack}
          type={playerType}
          poster={poster}
          currentChannel={refCurrentChannel.current}
          onVideoLoad={handleOnVideoLoad}
          onGetDuration={handleOnGetDuration}
          onPlayEnd={handleOnPlayEnd}
          onTimeProgress={onTimeProgress}
          handleOnLanguageChange={handleOnLanguageChange}
          handleOnSpeedChange={handleOnSpeedChange}
          handleOnSkipChange={handleOnSkipChange}
          handleOnResolutionChange={handleOnResolutionChange}
          initialTime={currentTime}
          onSetCurrentChannel={current => {
            refCurrentChannel.current = current;
          }}
          onVideoPlay={handleOnVideoPlay}
          memberVideoQuality={
            (member?.videoQuality || EQuality.AUTO) as EQuality
          }
        />
      </PaywallChecker>
    </div>
  );
};

export default AppVideoPlayer;
